"use client";

import { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { paintEgypt } from "@/components/guide-public/guide-explorer";
import { calloutElement } from "@/components/guide-public/guide-map-engine";

const TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "";

export type VenueMapNearby = {
  slug: string;
  name: string;
  meta: string;
  href: string;
  latitude: number;
  longitude: number;
};

/**
 * The venue page's own map: the venue's red pin with the real streets around
 * it, and the Guide's other selections nearby as gold dots that open their
 * pages. Without WebGL or a token the still map stands in.
 */
export function GuideVenueMap({
  name,
  latitude,
  longitude,
  nearby,
  staticSrc,
  directions,
  viewLabel,
  nearbyLabel,
}: {
  name: string;
  latitude: number;
  longitude: number;
  nearby: VenueMapNearby[];
  /** The still map, for when the live one can't load. */
  staticSrc: string | null;
  directions: string | null;
  /** "View venue", for the nearby callouts. */
  viewLabel: string;
  /** "Also in the Selection", for the nearby pins' accessible names. */
  nearbyLabel: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapFailed, setMapFailed] = useState(!TOKEN);

  useEffect(() => {
    if (!TOKEN || !containerRef.current) return;
    let disposed = false;
    let map: { remove: () => void } | null = null;
    (async () => {
      try {
        const mapboxgl = (await import("mapbox-gl")).default;
        if (disposed || !containerRef.current) return;
        mapboxgl.accessToken = TOKEN;
        const instance = new mapboxgl.Map({
          container: containerRef.current,
          style: "mapbox://styles/mapbox/light-v11",
          center: [longitude, latitude],
          zoom: 14.5,
          attributionControl: true,
          cooperativeGestures: true,
        });
        instance.on("style.load", () => paintEgypt(instance as unknown as Parameters<typeof paintEgypt>[0]));
        const dir = containerRef.current.closest(".eg-guide")?.getAttribute("dir") === "rtl" ? "rtl" : "ltr";
        instance.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

        const pin = document.createElement("div");
        pin.className = "eg-venue-pin";
        pin.setAttribute("role", "img");
        pin.setAttribute("aria-label", name);
        new mapboxgl.Marker({ element: pin, anchor: "bottom", offset: [0, 4] }).setLngLat([longitude, latitude]).addTo(instance);

        let popup: { remove: () => void } | null = null;
        for (const v of nearby) {
          const el = document.createElement("button");
          el.type = "button";
          el.className = "eg-nearby-pin";
          el.setAttribute("aria-label", `${nearbyLabel}: ${v.name}`);
          el.addEventListener("click", (event) => {
            event.stopPropagation();
            popup?.remove();
            popup = new mapboxgl.Popup({ offset: 14, maxWidth: "none", focusAfterOpen: false })
              .setLngLat([v.longitude, v.latitude])
              .setDOMContent(calloutElement({ title: v.name, meta: v.meta, href: v.href, linkLabel: viewLabel, dir }))
              .addTo(instance);
          });
          new mapboxgl.Marker({ element: el }).setLngLat([v.longitude, v.latitude]).addTo(instance);
        }
        if (nearby.length > 0) {
          const lngs = [longitude, ...nearby.map((v) => v.longitude)];
          const lats = [latitude, ...nearby.map((v) => v.latitude)];
          instance.fitBounds(
            [
              [Math.min(...lngs), Math.min(...lats)],
              [Math.max(...lngs), Math.max(...lats)],
            ],
            { padding: 56, maxZoom: 15, duration: 0 },
          );
        }
        map = instance;
      } catch {
        setMapFailed(true);
      }
    })();
    return () => {
      disposed = true;
      map?.remove();
    };
    // Built once for this venue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mapFailed) {
    if (!staticSrc) return null;
    const img = (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={staticSrc} alt={name} loading="lazy" style={{ display: "block", width: "100%", height: "auto", borderRadius: 4 }} />
    );
    return directions ? (
      <a href={directions} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
        {img}
      </a>
    ) : (
      img
    );
  }
  return <div dir="ltr" ref={containerRef} className="eg-venue-map" aria-label={name} />;
}
